import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Sparkle, BookOpen, ArrowDown, Spinner, CheckCircle, Image as ImageIcon, Warning, Storefront, Package } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/utils/api';
import StyledHtmlPreview from './StyledHtmlPreview';
import EbookSalesBundleModal from './EbookSalesBundleModal';
import BusinessMemoryBanner from './BusinessMemoryBanner';
import NextStepGuide from './NextStepGuide';
import EditablePreview from './EditablePreview';
import useBusinessMemoryPrefill from '@/hooks/useBusinessMemoryPrefill';
import useFoundationAutoStart from '@/hooks/useFoundationAutoStart';
import useFoundationAutoRoute from '@/hooks/useFoundationAutoRoute';
import { downloadBlob, downloadFromApi, isIOS } from '@/utils/downloadBlob';

/**
 * EbookBuilder — Pro-tier Done-For-You ebook generator.
 * Produces a 6-10 chapter, cover-illustrated, multi-page PDF via WeasyPrint.
 */
export default function EbookBuilder({ isOpen, onClose, onPickStep, onSuccess, user }) {
  const [step, setStep] = useState('input'); // 'input' | 'generating' | 'result'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // Kept for the legacy status banner — always 'ready' now because Pexels
  // cover (or branded fallback) is resolved during /ebook/generate.
  const [imagesStatus, setImagesStatus] = useState('none');

  const [topic, setTopic] = useState('');
  const [targetReader, setTargetReader] = useState('');
  const [numChapters, setNumChapters] = useState(8);
  const [tone, setTone] = useState('friendly and authoritative');
  const [showBundleModal, setShowBundleModal] = useState(false);

  // Prefill from the user's saved business memory when the modal opens. The
  // user's own typing always wins — we only fill when the field is empty.
  // (tone has a non-empty default, so it isn't auto-filled from memory.)
  useBusinessMemoryPrefill(isOpen, {
    topic: [topic, setTopic],
    targetReader: [targetReader, setTargetReader],
  });

  // Round 218A Phase 8D-3D — auto-consume Foundation when complete.
  useFoundationAutoStart({
    isOpen,
    ready: !!(topic && targetReader),
    step,
    onStart: () => handleGenerate(),
  });

  // Round 218A P0 — foundation gate.
  useFoundationAutoRoute({ isOpen, onClose });

  useEffect(() => {
    if (!isOpen) {
      setStep('input');
      setResult(null);
      setImagesStatus('none');
      setShowBundleModal(false);
    }
  }, [isOpen]);

  const handleGenerate = async () => {
    if (!topic.trim() || !targetReader.trim()) {
      toast.error('Please fill in both topic and target reader');
      return;
    }
    setStep('generating');
    setLoading(true);
    try {
      const response = await api.post(
        '/ebook/generate',
        { topic: topic.trim(), target_reader: targetReader.trim(), num_chapters: numChapters, tone },
        { timeout: 180000 },
      );
      setResult(response.data);
      // Cover is fetched from Pexels synchronously during /ebook/generate,
      // so the ebook is 100% ready the moment this call returns. No image
      // polling needed anymore.
      setImagesStatus('ready');
      setStep('result');
      toast.success(response.data.tier_message || 'Your ebook is ready!');
      // Round 218D P0 (2026-02-07) — canonical resolver-driven
      // continue flow. DashboardPage owns the CTA.
      try { onSuccess?.({ meta: { title: 'Your ebook is ready.' } }); } catch (_) { /* noop */ }
    } catch (error) {
      console.error('Ebook generation failed', error);
      toast.error(error.response?.data?.detail || "Couldn't generate your ebook. Please try again.");
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!result?.id) return;
    const safe = (result.title || 'ebook').replace(/[^a-z0-9. _-]/gi, '_');
    try {
      const res = await downloadFromApi(
        api,
        `/ebook/${result.id}/download`,
        `${safe}.pdf`,
        { method: 'GET', mime: 'application/pdf' },
      );
      if (res.ios) {
        toast.success("PDF opened — tap Share → Save to Files to keep it.", { duration: 6000 });
      } else {
        toast.success('PDF download started');
      }
    } catch (err) {
      console.error('[EbookBuilder] PDF download failed', err);
      toast.error(err.response?.data?.detail || "Couldn't download PDF. Please try again.");
    }
  };

  const downloadDocx = async () => {
    if (!result?.id) return;
    const safe = (result.title || 'ebook').replace(/[^a-z0-9. _-]/gi, '_');
    try {
      const res = await downloadFromApi(
        api,
        `/ebook/${result.id}/download.docx`,
        `${safe}.docx`,
        {
          method: 'GET',
          mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        },
      );
      if (res.ios) {
        toast.success("DOCX opened — tap Share → Save to Files.", { duration: 6000 });
      } else {
        toast.success('DOCX download started');
      }
    } catch (err) {
      console.error('[EbookBuilder] DOCX download failed', err);
      toast.error(err.response?.data?.detail || "Couldn't download DOCX. Please try again.");
    }
  };

  // Universal Editable Preview Mode (Phase 1) — save-final handler.
  // Persists ONLY the editable whitelist server-side. Business Memory
  // is NEVER touched by this call — see /api/ebook/{id} PATCH server
  // logic + tests/test_ebook_editable_preview.py.
  const [savingEdits, setSavingEdits] = useState(false);
  const editableValue = useMemo(() => ({
    title: result?.title || '',
    subtitle: result?.subtitle || '',
    author: result?.author || '',
    target_reader: result?.target_reader || '',
    tone: result?.tone || '',
    chapters: result?.chapters || [],
    cta_page: result?.cta_page || null,
  }), [result]);

  const handleSaveEdits = useCallback(async (next) => {
    if (!result?.id) return;
    setSavingEdits(true);
    try {
      // Whitelist on the client too — defence in depth. Even if the user
      // somehow injects extra keys, the server will drop them, but we
      // also avoid sending them at all.
      const body = {
        title: next.title,
        subtitle: next.subtitle,
        author: next.author,
        target_reader: next.target_reader,
        tone: next.tone,
        chapters: next.chapters,
        cta_page: next.cta_page,
      };
      const { data } = await api.patch(`/ebook/${result.id}`, body, { timeout: 30000 });
      // Reflect the saved state in our local result so re-preview / re-download
      // pick up the edits immediately.
      setResult((prev) => (prev ? { ...prev, ...body, last_edited_at: data?.saved_at } : prev));
    } finally {
      setSavingEdits(false);
    }
  }, [result?.id]);

  const downloadHtml = async () => {
    /* Phase G — V2 grants export to every subscriber */
    if (!result?.html_version) return;
    const safe = (result.title || 'ebook').replace(/[^a-z0-9. _-]/gi, '_');
    const blob = new Blob([result.html_version], { type: 'text/html' });
    const res = await downloadBlob(blob, `${safe}.html`);
    if (res.ios) toast.success('HTML opened — use Share → Save to Files.', { duration: 5000 });
  };

  const [exportingBundle, setExportingBundle] = useState(false);
  const downloadStorefrontBundle = async () => {
    /* Phase G — V2 grants export to every subscriber */
    if (!result?.id) return;
    setExportingBundle(true);
    const safe = (result.title || 'ebook').replace(/[^a-z0-9._-]/gi, '_');
    try {
      const res = await downloadFromApi(
        api,
        `/ebook/${result.id}/storefront-bundle`,
        `${safe}_storefront_bundle.zip`,
        { method: 'POST', data: {}, mime: 'application/zip' },
      );
      if (res.ios) {
        toast.success("Bundle opened — tap Share → Save to Files.", { duration: 6000 });
      } else {
        toast.success('Storefront bundle downloaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't build storefront bundle. Please try again.");
    } finally {
      setExportingBundle(false);
    }
  };

  const downloadTextVersion = async () => {
    /* Phase G — V2 grants export to every subscriber */
    if (!result?.text_version) return;
    const safe = (result.title || 'ebook').replace(/[^a-z0-9. _-]/gi, '_');
    const blob = new Blob([result.text_version], { type: 'text/plain' });
    const res = await downloadBlob(blob, `${safe}.txt`);
    if (res.ios) toast.success('Text opened — use Share → Save to Files.', { duration: 5000 });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-6" data-testid="ebook-builder-modal">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-3xl paygbot-modal-shell bg-gradient-to-br from-slate-900 to-slate-950 border border-white/10 rounded-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-br from-slate-900/95 to-slate-950/95 backdrop-blur-sm border-b border-white/10 p-4 sm:p-5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
              <BookOpen size={20} className="text-white" weight="fill" />
            </div>
            <div>
              <h2 className="text-white text-lg font-bold">Ebook Builder</h2>
              <p className="text-slate-400 text-xs">Done-for-you multi-page ebook — ready to sell</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-400" data-testid="ebook-builder-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-5">
          {step === 'input' && (
            <>
              <BusinessMemoryBanner />
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                <p className="text-purple-100 text-sm leading-relaxed">
                  Create a polished, niche-specific ebook with a styled cover, 6-10 chapters, pull-quotes,
                  action steps and per-chapter illustrations. Takes ~30 sec for text + ~30 sec for images.
                </p>
              </div>

              <div>
                <label className="text-white text-sm font-medium mb-1.5 block">Topic / Title Theme</label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. How to book 3× more cleaning clients"
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:border-purple-400"
                  data-testid="ebook-topic-input"
                />
              </div>

              <div>
                <label className="text-white text-sm font-medium mb-1.5 block">Target Reader</label>
                <textarea
                  rows={2}
                  value={targetReader}
                  onChange={(e) => setTargetReader(e.target.value)}
                  placeholder="e.g. Owners of solo residential cleaning services who want consistent monthly bookings"
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:border-purple-400 resize-none"
                  data-testid="ebook-target-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-white text-sm font-medium mb-1.5 block">Number of Chapters</label>
                  <select
                    value={numChapters}
                    onChange={(e) => setNumChapters(Number(e.target.value))}
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-purple-400"
                    data-testid="ebook-chapters-select"
                  >
                    {[6, 7, 8, 9, 10].map(n => <option key={n} value={n} className="bg-slate-900">{n} chapters</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-1.5 block">Tone of Voice</label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-purple-400"
                    data-testid="ebook-tone-select"
                  >
                    <option value="friendly and authoritative" className="bg-slate-900">Friendly &amp; authoritative</option>
                    <option value="warm and conversational" className="bg-slate-900">Warm &amp; conversational</option>
                    <option value="premium and high-ticket" className="bg-slate-900">Premium &amp; high-ticket</option>
                    <option value="bold and direct" className="bg-slate-900">Bold &amp; direct</option>
                    <option value="simple and beginner-friendly" className="bg-slate-900">Simple &amp; beginner-friendly</option>
                  </select>
                </div>
              </div>

              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-colors disabled:opacity-60"
                data-testid="ebook-generate-btn"
              >
                <Sparkle size={18} weight="fill" />
                <span>Generate Ebook</span>
              </button>
            </>
          )}

          {step === 'generating' && (
            <div className="py-12 flex flex-col items-center justify-center space-y-4">
              <Spinner size={44} className="text-purple-400 animate-spin" />
              <p className="text-white font-medium">Writing your {numChapters}-chapter ebook…</p>
              <p className="text-slate-400 text-xs text-center max-w-sm">
                This takes about 30 seconds. We are generating niche-specific chapters,
                action steps, worksheets and a professional cover.
              </p>
            </div>
          )}

          {step === 'result' && result && (
            <>
              {/* Summary card */}
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle size={22} className="text-emerald-400 flex-shrink-0 mt-0.5" weight="fill" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-base truncate" data-testid="ebook-title-output">{result.title}</h3>
                    {result.subtitle && <p className="text-slate-300 text-sm mt-1">{result.subtitle}</p>}
                    <p className="text-slate-400 text-xs mt-2">
                      By {result.author} · {(result.chapters || []).length} chapters
                    </p>
                  </div>
                </div>
              </div>

              {/* Tier-specific status banner.
                  - Starter: plain text ebook ready.
                  - Growth:  editable template ready (Pexels OR gradient fallback cover).
                  - Pro:     finished ebook ready (Pexels OR gradient fallback cover).
                  No polling, no spinner — cover is fetched during generation. */}
              <div
                className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center space-x-3"
                data-testid="ebook-tier-status"
              >
                <CheckCircle size={18} className="text-emerald-400" weight="fill" />
                <div className="flex-1 text-sm">
                  <span className="text-emerald-300 font-medium">
                    {result.tier_message || 'Your ebook is ready.'}
                  </span>
                  {result.cover_image_source === 'pexels' && result.cover_attribution && (
                    <span className="text-slate-500 text-xs ml-2">· {result.cover_attribution}</span>
                  )}
                  {result.cover_image_source === 'fallback' && (
                    <span className="text-slate-500 text-xs ml-2">· Branded cover</span>
                  )}
                </div>
                <ImageIcon size={14} className="text-slate-500" />
              </div>

              {result.html_version && (
                <StyledHtmlPreview
                  html={result.html_version}
                  title="Your Ebook — Live Preview"
                  downloadName={`${(result.title || 'ebook').replace(/[^a-z0-9._-]/gi, '_')}.html`}
                />
              )}

              {/* Starter tier: plain text preview */}
              {!result.html_version && result.text_version && (
                <div
                  className="bg-white/5 border border-white/10 rounded-xl p-4"
                  data-testid="ebook-text-preview"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white text-sm font-semibold">Your Ebook — Plain Text</h4>
                    <span className="text-[11px] text-slate-500 px-2 py-0.5 rounded bg-white/5 border border-white/10">
                      Starter · words only
                    </span>
                  </div>
                  <pre className="text-slate-200 text-[13px] whitespace-pre-wrap font-sans leading-relaxed max-h-[420px] overflow-y-auto">
{result.text_version}
                  </pre>
                </div>
              )}

              {/* Round 218A Phase 8A — Editable Preview is available
                  to every paying customer under the unified Strategist
                  model. Removed `result.plan_tier === 'pro'` gate. */}
              {result.html_version && (
                <>
                  {/* Universal Editable Preview Mode (Phase 1 reference impl) —
                      lets the user fix the price, headline, chapter wording etc.
                      BEFORE downloading. Edits autosave to localStorage every
                      10s and only persist server-side on Save Final. Business
                      Memory is never touched. */}
                  <EditablePreview
                    draftKey={`ebook-draft-${result.id}`}
                    originalValue={editableValue}
                    onSave={handleSaveEdits}
                    busy={savingEdits}
                    savedAt={result.last_edited_at || null}
                    downloads={[
                      { label: 'Download PDF', onClick: downloadPdf, format: 'PDF', testid: 'ebook-download-pdf-btn' },
                      { label: 'Download DOCX', onClick: downloadDocx, format: 'DOCX', testid: 'ebook-download-docx-btn' },
                    ]}
                    previewSlot={({ value }) => (
                      <div className="space-y-3 max-h-[420px] overflow-y-auto">
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Title</p>
                          <p className="text-white text-base font-semibold">{value.title || '—'}</p>
                          {value.subtitle && <p className="text-slate-400 text-sm">{value.subtitle}</p>}
                        </div>
                        {value.author && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Author</p>
                            <p className="text-slate-200 text-sm">{value.author}</p>
                          </div>
                        )}
                        {value.target_reader && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Target reader</p>
                            <p className="text-slate-300 text-sm">{value.target_reader}</p>
                          </div>
                        )}
                        {(value.chapters || []).slice(0, 3).map((c, i) => (
                          <div key={`pv-ch-${i}`}>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Chapter {i + 1}</p>
                            <p className="text-white text-sm font-medium">{c?.title || '—'}</p>
                            <p className="text-slate-400 text-xs line-clamp-3">{c?.body || ''}</p>
                          </div>
                        ))}
                        {(value.chapters || []).length > 3 && (
                          <p className="text-slate-500 text-xs italic">
                            + {value.chapters.length - 3} more chapters
                          </p>
                        )}
                        {value.cta_page?.headline && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">CTA</p>
                            <p className="text-emerald-300 text-sm font-medium">{value.cta_page.headline}</p>
                          </div>
                        )}
                      </div>
                    )}
                    editSlot={({ value, setValue }) => (
                      <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Title</span>
                          <input
                            type="text"
                            value={value.title || ''}
                            onChange={(e) => setValue({ ...value, title: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-400 focus:outline-none"
                            data-testid="ebook-edit-title"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Subtitle</span>
                          <input
                            type="text"
                            value={value.subtitle || ''}
                            onChange={(e) => setValue({ ...value, subtitle: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-400 focus:outline-none"
                            data-testid="ebook-edit-subtitle"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">Author</span>
                          <input
                            type="text"
                            value={value.author || ''}
                            onChange={(e) => setValue({ ...value, author: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-400 focus:outline-none"
                            data-testid="ebook-edit-author"
                          />
                        </label>
                        <div className="space-y-3">
                          <p className="text-[11px] uppercase tracking-wide text-slate-500">Chapters</p>
                          {(value.chapters || []).map((c, i) => (
                            <div key={`ed-ch-${i}`} className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
                              <input
                                type="text"
                                value={c?.title || ''}
                                onChange={(e) => {
                                  const next = [...value.chapters];
                                  next[i] = { ...next[i], title: e.target.value };
                                  setValue({ ...value, chapters: next });
                                }}
                                placeholder={`Chapter ${i + 1} title`}
                                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm focus:border-purple-400 focus:outline-none"
                                data-testid={`ebook-edit-chapter-${i}-title`}
                              />
                              <textarea
                                rows={4}
                                value={c?.body || ''}
                                onChange={(e) => {
                                  const next = [...value.chapters];
                                  next[i] = { ...next[i], body: e.target.value };
                                  setValue({ ...value, chapters: next });
                                }}
                                placeholder="Chapter body…"
                                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-slate-200 text-sm focus:border-purple-400 focus:outline-none"
                                data-testid={`ebook-edit-chapter-${i}-body`}
                              />
                            </div>
                          ))}
                        </div>
                        {value.cta_page && (
                          <div className="space-y-2 bg-white/5 border border-white/10 rounded-lg p-3">
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">CTA page</p>
                            <input
                              type="text"
                              value={value.cta_page.headline || ''}
                              onChange={(e) => setValue({ ...value, cta_page: { ...value.cta_page, headline: e.target.value } })}
                              placeholder="Headline"
                              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm focus:border-emerald-400 focus:outline-none"
                              data-testid="ebook-edit-cta-headline"
                            />
                            <textarea
                              rows={3}
                              value={value.cta_page.body || ''}
                              onChange={(e) => setValue({ ...value, cta_page: { ...value.cta_page, body: e.target.value } })}
                              placeholder="CTA body"
                              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-slate-200 text-sm focus:border-emerald-400 focus:outline-none"
                              data-testid="ebook-edit-cta-body"
                            />
                            <input
                              type="text"
                              value={value.cta_page.cta_text || ''}
                              onChange={(e) => setValue({ ...value, cta_page: { ...value.cta_page, cta_text: e.target.value } })}
                              placeholder='CTA button text e.g. "Get the Bonus Pack"'
                              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm focus:border-emerald-400 focus:outline-none"
                              data-testid="ebook-edit-cta-text"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  />

                  {/* Legacy quick-download row — kept for users who don't
                      want to enter edit mode at all. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      onClick={downloadHtml}
                      className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors"
                      data-testid="ebook-download-html-btn"
                    >
                      <ArrowDown size={14} />
                      <span>Download HTML</span>
                    </button>
                  </div>
                  <p className="text-slate-500 text-xs text-center">
                    Edit your ebook above before downloading — autosaves every 10 s, never overwrites your business profile.
                  </p>
                </>
              )}

              {/* Round 218A Phase 8A — Under the unified Strategist
                  model there is one ebook experience for every
                  paying customer. Removed the three tier-specific
                  download blocks (starter/growth/pro) and their
                  upsell copy. Everyone who has an ebook can download
                  the HTML preview + plain-text version. The
                  finished PDF path continues to run through the
                  standard export flow. */}
              {result.html_version && (
                <button
                  onClick={downloadHtml}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white text-sm font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors"
                  data-testid="ebook-download-html-btn"
                >
                  <ArrowDown size={14} />
                  <span>Download HTML</span>
                </button>
              )}
              {result.text_version && (
                <button
                  onClick={downloadTextVersion}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white text-sm font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors"
                  data-testid="ebook-download-text-btn"
                >
                  <ArrowDown size={14} />
                  <span>Download Plain Text (.txt)</span>
                </button>
              )}
              <p className="text-slate-500 text-xs text-center">
                Edit before downloading — autosaves every 10s, never overwrites your business profile.
              </p>

              {/* Create Sales Bundle upsell */}
              <button
                onClick={() => setShowBundleModal(true)}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-sm font-semibold rounded-xl flex items-center justify-center space-x-2 transition-colors"
                data-testid="ebook-create-sales-bundle-btn"
              >
                <Storefront size={16} weight="fill" />
                <span>Create Sales Bundle — Ready for Stan Store / Gumroad</span>
              </button>
              <p className="text-slate-500 text-[11px] text-center -mt-2">
                Auto-generates product title, description, bullets, social caption &amp; launch email.
              </p>

              {/* Round 35 — "How to use this" tier-aware execution
                  guidance pulled from result.ebook_execution_guide
                  (populated server-side from get_business_system).
                  Shows users exactly where to sell + promote.            */}
              {(() => {
                const guide = result?.ebook_execution_guide;
                const where = guide?.where_to_use?.length
                  ? guide.where_to_use
                  : [
                      'Sell on Etsy, Gumroad, or Stan Store',
                      'Use as a free lead magnet',
                      'Promote on Instagram, Facebook, or Nextdoor',
                    ];
                return (
                  <div
                    data-testid="ebook-how-to-use"
                    className="rounded-xl border border-white/10 bg-white/5 p-3 -mt-1"
                  >
                    <p className="text-[11px] font-semibold text-emerald-300 uppercase tracking-wide mb-1.5">
                      How to use this
                    </p>
                    <ul className="space-y-1 text-[12px] text-slate-200">
                      {where.slice(0, 4).map((line, i) => (
                        <li
                          key={i}
                          data-testid={`ebook-how-to-use-line-${i}`}
                          className="flex gap-2"
                        >
                          <span className="text-emerald-400">•</span>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                    {/* Round 218A Phase 8A — Removed the 3 per-tier
                        "note" strings (Pro/Growth/Starter). Under
                        the unified Strategist model every customer
                        gets the same ready-to-use asset. */}
                  </div>
                );
              })()}

              {/* Export Storefront Bundle (ZIP) */}
              <button
                onClick={downloadStorefrontBundle}
                disabled={exportingBundle}
                className="w-full py-3 bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-semibold rounded-xl flex items-center justify-center space-x-2 transition-colors disabled:opacity-60"
                data-testid="ebook-export-storefront-bundle-btn"
              >
                <Package size={16} weight="fill" />
                <span>{exportingBundle ? 'Building ZIP…' : 'Export Storefront Bundle (.zip)'}</span>
              </button>
              <p className="text-slate-500 text-[11px] text-center -mt-2">
                ZIP with listing, cover image, launch email &amp; social caption — drop into Gumroad/Stan Store.
              </p>

              <button
                onClick={() => { setStep('input'); setResult(null); }}
                className="w-full py-2 text-slate-400 hover:text-white text-sm"
                data-testid="ebook-new-btn"
              >
                Create another ebook
              </button>

              {/* Next Step Guide — keeps users inside the build loop */}
              <div className="pt-3 border-t border-white/10">
                <NextStepGuide
                  currentStep="ebook"
                  onPick={(stepKey) => {
                    setStep('input');
                    setResult(null);
                    onClose();
                    onPickStep?.(stepKey);
                  }}
                  onDone={onClose}
                />
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Sales Bundle modal */}
      <EbookSalesBundleModal
        isOpen={showBundleModal}
        onClose={() => setShowBundleModal(false)}
        ebookId={result?.id}
        ebookTitle={result?.title}
        onFindViralIdeas={() => {
          // Close ebook builder and route directly into Find Viral Ideas so
          // the user stays inside the workflow (no dashboard bounce).
          setShowBundleModal(false);
          onClose();
          if (onPickStep) onPickStep('viral_ideas');
        }}
        onCreateContent={() => {
          setShowBundleModal(false);
          onClose();
          if (onPickStep) onPickStep('content');
        }}
      />
    </div>
  );
}
